import { map, catchError, switchMap, withLatestFrom, tap } from 'rxjs/operators';
import { ActionType, HttpErrorMessage } from '@aston/foundation';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { TranslateService } from '@ngx-translate/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Injectable, inject } from '@angular/core';
import { EMPTY, Observable, of, zip } from 'rxjs';
import { Store, Action } from '@ngrx/store';

import { EmailViewerModalComponent } from '../../../shared-module/components/email-viewer-modal';
import { PdfViewerComponent } from '../../../shared-module/components/pdf-viewer';
import { extractFromFormPayload, mapToDisplayedContact } from '../../../dunnings-module/functions';
import { SuperDebtorDetailService } from '../../../debtors-module/services';
import { ThirdPartyContactsService } from '../../../shared-module/services';
import { DunningActionsService } from '../../../dunnings-module/services';
import { cleanTemplateBody } from '../../../dunnings-module/functions';
import * as AppStoreActions from '../../app-store/actions';
import { AppConstants } from '../../../app.constants';
import * as featureSelectors from '../selectors';
import * as featureActions from '../actions';
import { SuperDebtorPageStore } from '../../../debtors-module/stores/super-debtor-page';
import { ISuperDebtorIdentity } from '../../../debtors-module/models';

@Injectable({
	providedIn: 'root'
})
export class DunningActionDetailStorePreviewEffects {

	private store = inject(Store);
	private actions$ = inject(Actions);
	private modalService = inject(NgbModal);
	private translateService = inject(TranslateService);
	private tpContactsService = inject(ThirdPartyContactsService);
	private dunningActionsService = inject(DunningActionsService);
	private superDebtorDetailService = inject(SuperDebtorDetailService);
	private sdPageStore = inject(SuperDebtorPageStore);

	previewDunningActionPostalMailRequest$: Observable<Action> = createEffect(() => this.actions$.pipe(
		ofType(featureActions.PreviewDunningActionPostalMailRequest, featureActions.PreviewDunningActionInHousePrintRequest),
		switchMap(action => this.dunningActionsService.getDunningActionPostalMailPreview(action.param).pipe(
			map(content => featureActions.PreviewDunningActionPostalMailSuccess(content, action.param)),
			catchError(error => of(featureActions.PreviewDunningActionFailure({param: error})))
		))
	));

	previewDunningActionEmailRequest$: Observable<Action> = createEffect(() => this.actions$.pipe(
		ofType(featureActions.PreviewDunningActionEmailRequest),
		withLatestFrom(
			this.store.select(featureSelectors.selectSuperDebtorId),
			this.sdPageStore.identityAsObservable(),
		),
		switchMap(([action, sid, identity]: [any, number, ISuperDebtorIdentity]) => zip([
			of(action),
			this.tpContactsService.fetchContactsIds([
				...(action.param.primaryContactIds || []),
				...(action.param.secondaryContactIds || []),
			]),
			this.superDebtorDetailService.getSuperDebtorContacts(sid, {}),
			// Bug 35797: L'adresse Email du centre payeur est absente
			identity.payingCenterId ? this.superDebtorDetailService.getSuperDebtorContacts(identity.payingCenterId, {}) : of([]),
		])),
		switchMap(([action, cc1, cc2, payingCenterContacts]) => {
			const allContacts = [...cc1, ...cc2, ...payingCenterContacts]
			// eslint-disable-next-line prefer-const
			let {to, toCc, extracted} = extractFromFormPayload(action.param, allContacts);
			if (extracted.length < action.param.primaryContactIds?.length + action.param.secondaryContactIds?.length) {
				console.debug(`[da form] some contacts not found`, action.param);
			}
			const param = { ...action.param, content: cleanTemplateBody(action.param.content) };

			// Bug 37444: Les informations "Type, référence et Fonction" sont absentes
			to = allContacts.filter(c => (action.param.primaryContactIds || []).includes(c.id)).map(c => mapToDisplayedContact(this.translateService, c).displayableName).filter(Boolean).join(', ')
			toCc = allContacts.filter(c => (action.param.secondaryContactIds || []).includes(c.id)).map(c => mapToDisplayedContact(this.translateService, c).displayableName).filter(Boolean).join(', ')

			return this.dunningActionsService.getDunningActionEmailPreview(param).pipe(
				map(content => featureActions.PreviewDunningActionEmailSuccess(content, param, to, toCc)),
				catchError(error => of(featureActions.PreviewDunningActionFailure({param: error})))
			);
		})
	));

	previewDunningActionShortMessageRequest$: Observable<Action> = createEffect(() => this.actions$.pipe(
		ofType(featureActions.PreviewDunningActionShortMessageRequest),
		withLatestFrom(
			this.store.select(featureSelectors.selectSuperDebtorId),
			this.sdPageStore.identityAsObservable(),
		),
		switchMap(([action, sid, identity]: [any, number, ISuperDebtorIdentity]) => zip([
			of(action),
			this.tpContactsService.fetchContactsIds([
				...(action.param.primaryContactIds || []),
				...(action.param.secondaryContactIds || []),
			]),
			this.superDebtorDetailService.getSuperDebtorContacts(sid, {}),
			// Bug 35797: L'adresse Email du centre payeur est absente
			identity.payingCenterId ? this.superDebtorDetailService.getSuperDebtorContacts(identity.payingCenterId, {}) : of([]),
		])),
		switchMap(([action, cc1, cc2, payingCenterContacts]) => {
			const {to, extracted} = extractFromFormPayload(action.param, [...cc1, ...cc2, ...payingCenterContacts]);
			if (extracted.length < action.param.primaryContactIds?.length + action.param.secondaryContactIds?.length) {
				console.debug(`[da form] some contacts not found`, action.param);
			}
			
			return this.dunningActionsService.getDunningActionEmailPreview(action.param).pipe(
				map(content => featureActions.PreviewDunningActionShortMessageSuccess(content, action.param, to)),
				catchError(error => of(featureActions.PreviewDunningActionFailure({param: error})))
			);
		})
	));

	previewDunningActionPostalMailSuccess$ = createEffect(() => this.actions$.pipe(
		ofType(featureActions.PreviewDunningActionPostalMailSuccess),
		withLatestFrom(this.store),
		map(([action]) => {
			const modalRef = this.modalService.open(PdfViewerComponent, { ...AppConstants.DEFAULT_MODAL_OPTIONS, modalDialogClass: 'ModalDialog-PreviewDunningActionPostalMail' });
			const modalInstance: PdfViewerComponent = modalRef.componentInstance;
			modalInstance.title = 'Dunning.Preview.Letter.Title';
			modalInstance.url = action.content;
			modalInstance.showAction = true;
			modalInstance.actionResult.pipe(
				catchError(reason => {
					this.store.dispatch(AppStoreActions.CloseConfirmationModal({actionType: reason}));
					return EMPTY;
				}),
				map(event => {
					modalRef.dismiss();

					const reason = event.type;
					if (reason === ActionType.SUBMIT) {
						this.store.dispatch(featureActions.SubmitFormCompleteDunning({entity: action.entity}));
					}

					if (reason === ActionType.CANCEL) {
						this.store.dispatch(AppStoreActions.CloseConfirmationModal({actionType: ActionType.CANCEL}));
					}
				})
			).subscribe();

			return AppStoreActions.Noop();
		})
	));

	previewDunningActionEmailSuccess$: Observable<Action> = createEffect(() => this.actions$.pipe(
		ofType(featureActions.PreviewDunningActionEmailSuccess, featureActions.PreviewDunningActionShortMessageSuccess),
		tap(action => {
			const modalRef = this.modalService.open(EmailViewerModalComponent, { ...AppConstants.DEFAULT_MODAL_OPTIONS, modalDialogClass: 'ModalDialog-PreviewDunningActionEmail' });
			const modalInstance: EmailViewerModalComponent = modalRef.componentInstance;

			modalInstance.email = action.content;
			modalInstance.email.to = action.to;
			modalInstance.showAction = true;

			if (action.type === featureActions.PreviewDunningActionEmailSuccess.type) {
				modalInstance.title = 'Dunning.Preview.Mail.Title';
				modalInstance.ccHelpText = 'Dunning.Completion.Mail.CCHelp';
				modalInstance.email.toCc = action.toCc;
			}

			if (action.type === featureActions.PreviewDunningActionShortMessageSuccess.type) {
				modalInstance.title = 'Dunning.Preview.ShortMessage.Title';
			}

			modalInstance.actionResult.pipe(
				catchError(reason => {
					this.store.dispatch(AppStoreActions.CloseConfirmationModal({actionType: reason}));
					return EMPTY;
				}),
				map(event => {
					modalRef.dismiss();

					const reason = event.type;
					if (reason === ActionType.SUBMIT) {
						this.store.dispatch(featureActions.SubmitFormCompleteDunning({entity: action.entity}));
					}

					if (reason === ActionType.CANCEL) {
						this.store.dispatch(AppStoreActions.CloseConfirmationModal({actionType: ActionType.CANCEL}));
					}
				})
			).subscribe();
		})
	), {dispatch: false});

	previewDunningAccountStatementRequest$: Observable<Action> = createEffect(() => this.actions$.pipe(
		ofType(featureActions.PreviewDunningAccountStatementRequest),
		withLatestFrom(this.store.select(featureSelectors.selectDunningAction)),
		switchMap(([action, dunningAction]) =>
			this.dunningActionsService.getDunningActionAccountStatement(dunningAction.id, action.languageCode).pipe(
				map(url => featureActions.PreviewDunningAccountStatementSuccess({param: url})),
				catchError(() => {
					const param = new HttpErrorMessage()
						.resetKey('WarningIncompleteDunningContactForStatement')
						.translate(this.translateService);
					return of(featureActions.PreviewDunningAccountStatementFailure({param}))
				})
			))
	));

	previewDunningPaymentScheduleRequest$: Observable<Action> = createEffect(() => this.actions$.pipe(
		ofType(featureActions.PreviewDunningPaymentScheduleRequest),
		withLatestFrom(this.store.select(featureSelectors.selectDunningAction)),
		switchMap(([action, dunningAction]) =>
			this.dunningActionsService.getDunningActionPaymentSchedule(dunningAction.id, action.params).pipe(
				map(url => featureActions.PreviewDunningPaymentScheduleSuccess({param: url})),
				catchError(() => {
					const param = new HttpErrorMessage()
						.resetKey('WarningIncompleteDunningContactForStatement')
						.translate(this.translateService);
					return of(featureActions.PreviewDunningPaymentScheduleFailure({param}))
				})
			))
	));

	previewDunningAccountStatementSuccess$: Observable<Action> = createEffect(() => this.actions$.pipe(
		ofType(featureActions.PreviewDunningAccountStatementSuccess, featureActions.PreviewDunningPaymentScheduleSuccess),
		map(action => AppStoreActions.OpenUrl({url: action.param, fileName: action.type}))
	));

}
